// @ts-strict-ignore
import * as React from 'react';

import {DropMenuReact} from 'common/components/drop-menu';
import {Directions} from 'common/components/drop-menu/types';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {CreateAnnotationForm} from 'common/widgets/create-annotation-form';
import {useAnnotations} from 'common/contexts/react/annotations-context';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {useTranslation} from 'common/i18n/hooks';
import {ButtonReact} from 'common/components/button';

import {AnnotationState} from '../types';
import {AnnotationViewType, ViewAnnotationsListReact} from './view-annotations-list';
import {Annotations} from './index';
import styles from './component.module.styl';
import {AnnotationLocation} from './tracking';

// until insights uses mp-line-chart any changes in here may
// need to be in iron/apps/insights/src/insights-app/insights-result/chart-display/line-chart/index.jade

export type AnnotationsProps = {
  annotations: AnnotationState;
  annotationsHelpers: ReturnType<typeof Annotations.getHelpers>;
  canShowAnnotations: boolean;
  timeUnit?: TimeUnit;
  utcOffset?: number;
};

export function AnnotationsComponent(props: AnnotationsProps) {
  const annotationsResource = useAnnotations();
  const {t} = useTranslation();

  const annotations = props.annotations;
  const annotationsHelpers = props.annotationsHelpers;
  return annotations.activeAnnotationButton ? (
    <DropMenuReact
      open={!annotations.activeAnnotationPopupClosing}
      direction={Directions.UpCenter}
      allowedFlipDirections={[Directions.UpRight, Directions.Up]}
      onChange={annotationsHelpers.popupChange}
      positionParent={annotations.activeAnnotationButton.element}
      key={`annotation-popup-${annotations.activeAnnotationButton.timestampMs}`}
      className=''
    >
      {annotations.creatingAnnotation ? (
        <CreateAnnotationForm
          key={annotations.createAnnotationDate}
          utcOffset={props.utcOffset}
          date={annotations.createAnnotationDate}
          onCreate={async ({annotation}) => {
            const annotationApiResponse = await annotationsResource.createAnnotation(annotation).catch(() => {
              addToast({message: t(`chart-actions.annotations.failed-create`), type: ToastType.ERROR});
            });
            if (annotationApiResponse) {
              annotationsHelpers.close();
            }
          }}
          showTagInput
          onClose={() => annotationsHelpers.close()}
          location={AnnotationLocation.Chart}
        />
      ) : annotations.viewingAnnotations ? (
        <>
          <ViewAnnotationsListReact
            className={styles.annotationsComponentList}
            activeAnnotationGroup={annotations.visibleAnnotations.get(annotations.activeAnnotationButton.timestampMs)}
            canWriteAnnotations={props.canShowAnnotations}
            timeUnit={props.timeUnit}
            location={AnnotationLocation.Chart}
          />
          <div className={styles.createButton}>
            <ButtonReact onClick={annotationsHelpers.showCreate}>{t(`chart-actions.add-annotation`)}</ButtonReact>
          </div>
        </>
      ) : null}
    </DropMenuReact>
  ) : annotations.activeAnnotationPreview ? (
    <DropMenuReact
      open
      direction={Directions.UpCenter}
      allowedFlipDirections={[Directions.UpRight, Directions.Up]}
      positionParent={annotations.activeAnnotationPreview.element}
      key={`annotation-preview-${annotations.activeAnnotationPreview.timestampMs}`}
    >
      <ViewAnnotationsListReact
        className={styles.annotationsComponentList}
        activeAnnotationGroup={annotations.visibleAnnotations.get(annotations.activeAnnotationPreview.timestampMs)}
        timeUnit={props.timeUnit}
        type={AnnotationViewType.PREVIEW}
        location={AnnotationLocation.Chart}
      />
    </DropMenuReact>
  ) : null;
}
